import { useState } from 'react';
import { CheckCircle2, Send } from 'lucide-react';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { DatePicker } from '@/components/ui/DatePicker';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { Textarea } from '@/components/ui/Textarea';
import { instituteToday } from '@/features/teacher/utils';
import { ApiRequestError } from '@/lib/apiClient';
import {
  LEAVE_TYPE_LABEL,
  LEAVE_TYPES,
  leaveRequestSchema,
  MAX_REASON_LENGTH,
  type LeaveRequestForm,
  type LeaveType,
} from '@/lib/leaveRequest.schema';

/** The child the request is for, as the card at the top of the form prints them. */
export interface LeaveSubject {
  fullName: string;
  className: string;
  rollNumber: string | null;
}

export interface ApplyLeaveModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Null while it is still loading; the form cannot be sent until it is known. */
  child: LeaveSubject | null;
  /**
   * Files the request through the panel's own `api/` folder — the parent
   * panel names the child in the path, the student panel is the child.
   */
  onSubmit: (payload: LeaveRequestForm) => Promise<unknown>;
  /** Refreshes the reader's own list once a request has actually landed. */
  onSubmitted: () => void;
  /** The reader is the child, not their family — "your class teacher". */
  forSelf?: boolean;
}

const TYPE_OPTIONS = LEAVE_TYPES.map((type) => ({
  value: type,
  label: LEAVE_TYPE_LABEL[type],
}));

/**
 * Asking the class teacher for a child to be excused.
 *
 * The request is written to `student_leave_requests` and appears in that
 * child's class teacher's queue — the family sees the answer come back on the
 * same screen. Past dates are allowed: "he was ill on Monday, here is the note"
 * is the commonest thing a family writes to a school about, and a form that
 * only took the future would send them back to a paper slip. The server
 * bounds the dates by the session instead, and says so if one falls outside it.
 *
 * The form opens on the school's today, not the device's: a phone on the wrong
 * date or zone otherwise starts the request on a day nobody meant.
 */
export function ApplyLeaveModal({
  isOpen,
  onClose,
  child,
  onSubmit,
  onSubmitted,
  forSelf = false,
}: ApplyLeaveModalProps) {
  const [type, setType] = useState<LeaveType>('MEDICAL');
  const [fromDate, setFromDate] = useState(instituteToday);
  const [toDate, setToDate] = useState(instituteToday);
  const [reason, setReason] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  function reset() {
    setType('MEDICAL');
    setFromDate(instituteToday());
    setToDate(instituteToday());
    setReason('');
    setFieldErrors({});
    setFormError(null);
    setIsSubmitted(false);
  }

  function handleClose() {
    reset();
    onClose();
  }

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (!child) return;

    const parsed = leaveRequestSchema.safeParse({ type, fromDate, toDate, reason });
    if (!parsed.success) {
      // Each message lands on its own field; the server checks all of this
      // again, and its answer replaces these if the two ever disagree.
      setFieldErrors(
        Object.fromEntries(
          parsed.error.issues.map((issue) => [String(issue.path[0]), issue.message]),
        ),
      );
      return;
    }

    setFieldErrors({});
    setFormError(null);
    setIsSaving(true);

    try {
      await onSubmit(parsed.data);
      setIsSubmitted(true);
      onSubmitted();
    } catch (caught) {
      // The server names the reason — dates outside the session, or a request
      // already covering these days — so it is shown verbatim.
      setFormError(
        caught instanceof ApiRequestError
          ? caught.message
          : 'Something went wrong. Please try again.',
      );
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Apply for leave"
      description={
        forSelf
          ? 'Send a request to your class teacher'
          : child
            ? `Send a request to ${child.fullName}'s class teacher`
            : 'Send a request to your child’s class teacher'
      }
      size="md"
    >
      {isSubmitted ? (
        <div className="flex flex-col items-center justify-center py-8 text-center">
          <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full border border-emerald-500/20 bg-emerald-500/10 text-emerald-500">
            <CheckCircle2 className="h-6 w-6" />
          </div>
          <h3 className="text-base font-bold text-slate-900 dark:text-white">Request sent</h3>
          <p className="mt-1 max-w-sm text-xs text-slate-500 dark:text-slate-400">
            {forSelf
              ? 'It is now with your class teacher.'
              : `It is now with ${child?.fullName ?? 'your child'}’s class teacher.`}{' '}
            You will see their answer on this page.
          </p>
          <Button className="mt-5" onClick={handleClose}>
            Done
          </Button>
        </div>
      ) : (
        <form onSubmit={(event) => void handleSubmit(event)} className="space-y-4 pt-1">
          {child && (
            <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 dark:border-white/5 dark:bg-[#14182e]">
              <div>
                <p className="text-base font-semibold text-slate-900 dark:text-white">
                  {child.fullName}
                </p>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {child.className} &bull; Roll No: {child.rollNumber ?? '—'}
                </p>
              </div>
              <div className="rounded-full border border-indigo-500/20 bg-indigo-500/10 px-2.5 py-1 text-xs font-medium text-indigo-500 dark:text-indigo-400">
                Student
              </div>
            </div>
          )}

          {formError && <Alert tone="critical" title="Could not send that request">{formError}</Alert>}

          <Select
            label="Reason for leave"
            required
            options={TYPE_OPTIONS}
            value={type}
            onChange={(event) => setType(event.target.value as LeaveType)}
            error={fieldErrors.type}
          />

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <DatePicker
              label="First day"
              required
              value={fromDate}
              onChange={(event) => {
                const nextFrom = event.target.value;
                setFromDate(nextFrom);
                if (toDate < nextFrom) {
                  setToDate(nextFrom);
                }
              }}
              error={fieldErrors.fromDate}
            />
            <DatePicker
              label="Last day"
              required
              min={fromDate || instituteToday()}
              value={toDate}
              onChange={(event) => setToDate(event.target.value)}
              error={fieldErrors.toDate}
              hint="Same as the first day for a single day off"
            />
          </div>

          <Textarea
            label="Tell the class teacher why"
            required
            rows={3}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder="Please give the class teacher the details…"
            maxLength={MAX_REASON_LENGTH}
            error={fieldErrors.reason}
            hint={`${reason.trim().length} of ${MAX_REASON_LENGTH} characters`}
          />

          <div className="flex items-center justify-end gap-2.5 border-t border-slate-100 pt-3 dark:border-white/5">
            <Button type="button" variant="secondary" onClick={handleClose} disabled={isSaving}>
              Cancel
            </Button>
            <Button
              type="submit"
              isLoading={isSaving}
              disabled={!child}
              leftIcon={<Send className="h-3.5 w-3.5" />}
            >
              {isSaving ? 'Sending…' : 'Send request'}
            </Button>
          </div>
        </form>
      )}
    </Modal>
  );
}
